import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useParams } from 'react-router-dom';
import { RouteErrorFallback } from '@/components/common/RouteErrorFallback';
import { PermissionGuard } from './PermissionGuard';
import { PrivateRoute } from './PrivateRoute';
import { HomeRoute, MainSiteOnly } from './TenantRoutes';
import { SupportSessionPage } from '@/features/support-session/SupportSessionPage';

export { AuthInit } from './AuthInit';
import { DashboardLayout } from '@/components/layout';
import { PublicSiteLayout } from '@/components/marketing/PublicSiteLayout';
import { AppLoader } from '@/components/common/AppLoader';
import { LoginPage } from '@/pages/LoginPage';
import { SignupPage } from '@/pages/SignupPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { SetPasswordPage } from '@/pages/SetPasswordPage';
import { PlaceholderPage } from '@/pages/PlaceholderPage';
import { DashboardPage } from '@/features/dashboard/components/DashboardPage';
import { RolesPage } from '@/features/roles/components/RolesPage';
import { CreateRolePage } from '@/features/roles/components/CreateRolePage';
import { PermissionsMatrixPage } from '@/features/roles/components/PermissionsMatrixPage';
import { UsersPage } from '@/features/users/components/UsersPage';
import { CreateUserPage } from '@/features/users/components/CreateUserPage';
import { AuditLogsPage } from '@/features/audit-logs/components/AuditLogsPage';
import { NotificationsPage } from '@/features/notifications/components/NotificationsPage';
import { SettingsPage } from '@/features/settings/components/SettingsPage';
import { SupportPage } from '@/features/support/components/SupportPage';
import { SupportTicketDetailPage } from '@/features/support/components/SupportTicketDetailPage';
import { OrganizationPage } from '@/features/organization/components/OrganizationPage';
import { BillingPage } from '@/features/billing/components/BillingPage';
import { BillingReturnPage } from '@/features/billing/components/BillingReturnPage';
import { ShiftManagementPage } from '@/features/shifts/components/ShiftManagementPage';
import { UpcomingShiftsPage } from '@/features/shifts/components/UpcomingShiftsPage';
import { MyShiftsPage } from '@/features/shifts/components/MyShiftsPage';
import { MyAttendancePage } from '@/features/attendance/components/MyAttendancePage';
import { MyTimeCorrectionsPage } from '@/features/attendance/components/MyTimeCorrectionsPage';
import { WallboardPage } from '@/features/wallboard/components/WallboardPage';
import { MyLeavesPage } from '@/features/leave/components/MyLeavesPage';
import { MyBonusesPage } from '@/features/bonuses/components/MyBonusesPage';
import { MySecurityDepositsPage } from '@/features/security-ledger/components/MySecurityDepositsPage';
import { MyDocumentsPage } from '@/features/documents/components/MyDocumentsPage';
import { MyLettersPage } from '@/features/hr/components/letters/MyLettersPage';
import { SignLetterPage } from '@/features/hr/components/letters/SignLetterPage';
import { MyPoliciesPage } from '@/features/hr/components/my-policies/MyPoliciesPage';
import { AcknowledgePolicyPage } from '@/features/hr/components/my-policies/AcknowledgePolicyPage';
import { MyCasesPage } from '@/features/hr/components/my-cases/MyCasesPage';
import { MyCasePage } from '@/features/hr/components/my-cases/MyCasePage';
import { RaiseGrievancePage } from '@/features/hr/components/my-cases/RaiseGrievancePage';
import { WorkspaceAttendancePage } from '@/features/attendance/components/WorkspaceAttendancePage';
import { WorkspaceTimeCorrectionsPage } from '@/features/attendance/components/WorkspaceTimeCorrectionsPage';
import { CompaniesPage } from '@/features/companies/components/CompaniesPage';
import { CompanyDetailPage } from '@/features/companies/components/CompanyDetailPage';
import { WorkspaceMembersPage } from '@/features/companies/components/WorkspaceMembersPage';
import { HrWorkspacePage } from '@/features/hr/components/HrWorkspacePage';
import { AnnouncementsPage } from '@/features/announcements/components/AnnouncementsPage';
import { WorkspaceLeavesPage } from '@/features/leave/components/WorkspaceLeavesPage';
import { DocumentsPage } from '@/features/documents/components/DocumentsPage';
import { CandidatesPage } from '@/features/candidates/components/CandidatesPage';
import { WorkspacePenaltiesPage } from '@/features/penalties/components/WorkspacePenaltiesPage';
import { WorkspaceBonusesPage } from '@/features/bonuses/components/WorkspaceBonusesPage';
import { WorkspaceExpensesPage } from '@/features/expenses/components/WorkspaceExpensesPage';
import { ExpenseAnalyticsPage } from '@/features/expenses/components/ExpenseAnalyticsPage';
import { MyExpensesPage } from '@/features/expense-claims/components/MyExpensesPage';
import { ExpenseApprovalsPage } from '@/features/expense-claims/components/ExpenseApprovalsPage';
import { WorkspaceSecurityLedgerPage } from '@/features/security-ledger/components/WorkspaceSecurityLedgerPage';
import { RunDetailPage } from '@/features/payroll/components/RunDetailPage';
import { BatchReadinessPage } from '@/features/payroll/components/BatchReadinessPage';
import { BatchDetailPage } from '@/features/payroll/components/BatchDetailPage';
import { RunListPage } from '@/features/payroll/components/RunListPage';
import { MySlipDetailPage } from '@/features/payroll-me/components/MySlipDetailPage';
import { TaxRuleListPage } from '@/features/tax-rules/components/TaxRuleListPage';
import { TaxRuleViewPage } from '@/features/tax-rules/components/TaxRuleViewPage';
import { TaxRuleFormPage } from '@/features/tax-rules/components/TaxRuleFormPage';
import { TaxRuleVersionHistoryPage } from '@/features/tax-rules/components/TaxRuleVersionHistoryPage';
import { OrgPayrollOverviewPage } from '@/features/payroll/components/OrgPayrollOverviewPage';
import { PayrollForecastPage } from '@/features/payroll/components/PayrollForecastPage';
import { PayrollPaymentHistoryPage } from '@/features/payroll/components/PayrollPaymentHistoryPage';
import { RunItemDetailPage } from '@/features/payroll/components/RunItemDetailPage';
import { AdjustmentFormPage } from '@/features/payroll/components/AdjustmentFormPage';
import { MyPayrollHomePage } from '@/features/payroll-me/components/MyPayrollHomePage';
import { ReportsPage } from '@/features/reports/components/ReportsPage';
import { BoardsPage } from '@/features/boards/components/BoardsPage';
import { BoardViewPage } from '@/features/boards/components/BoardViewPage';
import { ChannelsPage } from '@/features/channels/components/ChannelsPage';
import { RequireFeature } from '@/features/feature-flags'
import { ImportHistoryPage } from '@/features/setup-wizard/components/ImportHistoryPage';
import { SetupWizardPage } from '@/features/setup-wizard/components/SetupWizardPage';
import { SetupWizardEntry } from '@/features/setup-wizard/components/SetupWizardEntry';

const LandingPage = lazy(() =>
  import('@/pages/LandingPage').then((module) => ({ default: module.LandingPage }))
);
const PricingPage = lazy(() =>
  import('@/pages/PricingPage').then((module) => ({ default: module.PricingPage }))
);

const loadLandingVersions = () => import('@/pages/landing-versions');
// On the home page, fetch the landing page alongside the app instead of after it.
if (typeof window !== 'undefined' && window.location.pathname === '/') {
  void loadLandingVersions();
}
const LandingV1Page = lazy(() =>
  loadLandingVersions().then((module) => ({ default: module.LandingV1Page }))
);
const LandingV2Page = lazy(() =>
  loadLandingVersions().then((module) => ({ default: module.LandingV2Page }))
);

/** The landing page draws its own background; a spinner would only flash. */
function LandingFallback(): React.ReactElement {
  return <div className="min-h-screen bg-white" aria-busy="true" />;
}

function PublicPageFallback(): React.ReactElement {
  return (
    <AppLoader label="Loading" />
  );
}

function CompanyLeavesRedirect(): React.ReactElement {
  const { companyId } = useParams();
  return (
    <Navigate
      to={companyId ? `/leaves?companyId=${encodeURIComponent(companyId)}` : '/leaves'}
      replace
    />
  );
}

const router = createBrowserRouter([
  {
    element: (
      <MainSiteOnly>
        <PublicSiteLayout />
      </MainSiteOnly>
    ),
    errorElement: <RouteErrorFallback />,
    children: [
      // The previous landing page, kept reachable while v2 settles in.
      {
        path: '/classic',
        element: (
          <Suspense fallback={<PublicPageFallback />}>
            <LandingPage />
          </Suspense>
        ),
      },
      {
        path: '/pricing',
        element: (
          <Suspense fallback={<PublicPageFallback />}>
            <PricingPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/v1',
    element: (
      <MainSiteOnly>
        <Suspense fallback={<PublicPageFallback />}>
          <LandingV1Page />
        </Suspense>
      </MainSiteOnly>
    ),
    errorElement: <RouteErrorFallback />,
  },
  // v2 is the home page; its old address forwards there.
  {
    path: '/',
    element: (
      <HomeRoute
        landing={
          <Suspense fallback={<LandingFallback />}>
            <LandingV2Page />
          </Suspense>
        }
      />
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/v2',
    element: <Navigate to="/" replace />,
  },
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/signup',
    element: (
      <MainSiteOnly>
        <SignupPage />
      </MainSiteOnly>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/set-password',
    element: <SetPasswordPage />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/support-session',
    element: <SupportSessionPage />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/',
    element: <PrivateRoute />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        element: <DashboardLayout />,
        errorElement: <RouteErrorFallback />,
        children: [
          {
            element: <PermissionGuard />,
            children: [
              { path: 'dashboard', element: <DashboardPage /> },
              { path: 'companies', element: <CompaniesPage /> },
              { path: 'companies/new', element: <Navigate to="/companies/setup-wizard/drafts/new" replace /> },
              { path: 'companies/setup-wizard', element: <SetupWizardEntry /> },
              { path: 'companies/setup-wizard/new', element: <Navigate to="/companies/setup-wizard/drafts/new" replace /> },
              { path: 'companies/setup-wizard/imports', element: <ImportHistoryPage /> },
              { path: 'companies/setup-wizard/drafts/new', element: <SetupWizardPage /> },
              { path: 'companies/setup-wizard/drafts/:draftId', element: <SetupWizardPage /> },
              { path: 'companies/:companyId/setup-wizard', element: <SetupWizardPage /> },
              { path: 'companies/:companyId', element: <CompanyDetailPage /> },
              { path: 'attendance', element: <WorkspaceAttendancePage /> },
              { path: 'time-corrections', element: <WorkspaceTimeCorrectionsPage /> },
              {
                path: 'companies/:companyId/attendance',
                element: <Navigate to="/attendance" replace />,
              },
              {
                path: 'companies/:companyId/attendance/time-corrections',
                element: <Navigate to="/time-corrections" replace />,
              },
              { path: 'shifts', element: <ShiftManagementPage /> },
              { path: 'shifts/upcoming', element: <UpcomingShiftsPage /> },
              { path: 'wallboard', element: <WallboardPage /> },
              { path: 'my-shifts', element: <MyShiftsPage /> },
              { path: 'my-attendance', element: <MyAttendancePage /> },
              { path: 'my-time-corrections', element: <MyTimeCorrectionsPage /> },
              { path: 'my-leaves', element: <MyLeavesPage /> },
              { path: 'my-bonuses', element: <MyBonusesPage /> },
              { path: 'my-deposits', element: <MySecurityDepositsPage /> },
              { path: 'my-documents', element: <MyDocumentsPage /> },
              { path: 'my-letters', element: <MyLettersPage /> },
              { path: 'my-letters/:documentId', element: <SignLetterPage /> },
              { path: 'my-policies', element: <MyPoliciesPage /> },
              { path: 'my-policies/:assignmentId', element: <AcknowledgePolicyPage /> },
              { path: 'my-hr-cases', element: <MyCasesPage /> },
              { path: 'my-hr-cases/new', element: <RaiseGrievancePage /> },
              { path: 'my-hr-cases/:caseId', element: <MyCasePage /> },
              {
                path: 'companies/:companyId/shifts/*',
                element: <Navigate to="/shifts" replace />,
              },
              { path: 'leaves', element: <WorkspaceLeavesPage /> },
              {
                path: 'companies/:companyId/leaves',
                element: <CompanyLeavesRedirect />,
              },
              { path: 'penalties', element: <WorkspacePenaltiesPage /> },
              {
                path: 'companies/:companyId/penalties',
                element: <Navigate to="/penalties" replace />,
              },
              {
                path: 'bonuses',
                element: <WorkspaceBonusesPage />,
              },
              {
                path: 'companies/:companyId/bonuses',
                element: <Navigate to="/bonuses" replace />,
              },
              { path: 'expenses', element: <WorkspaceExpensesPage /> },
              {
                path: 'companies/:companyId/expenses',
                element: <Navigate to="/expenses" replace />,
              },
              { path: 'expense-analytics', element: <ExpenseAnalyticsPage /> },
              { path: 'my-expenses', element: <MyExpensesPage /> },
              { path: 'expense-approvals', element: <ExpenseApprovalsPage /> },
              {
                path: 'security-ledger',
                element: <WorkspaceSecurityLedgerPage />,
              },
              {
                path: 'companies/:companyId/security-ledger',
                element: <Navigate to="/security-ledger" replace />,
              },
              { path: 'documents', element: <DocumentsPage /> },
              { path: 'candidates', element: <CandidatesPage /> },
              {
                path: 'companies/:companyId/documents',
                element: <Navigate to="/documents" replace />,
              },
              {
                path: 'companies/:companyId/payroll/runs/:runId',
                element: <RunDetailPage />,
              },
              { path: 'leave', element: <PlaceholderPage /> },
              { path: 'payroll', element: <PayrollForecastPage /> },
              { path: 'payroll/forecast', element: <PayrollForecastPage /> },
              { path: 'payroll/history', element: <PayrollPaymentHistoryPage /> },
              { path: 'payroll/history/:snapshotId', element: <PayrollPaymentHistoryPage /> },
              {
                path: 'payroll/forecast/items/:companyId/:employeeId',
                element: <RunItemDetailPage />,
              },
              { path: 'payroll/overview', element: <OrgPayrollOverviewPage /> },
              {
                path: 'payroll/batches/new',
                element: <BatchReadinessPage />,
              },
              {
                path: 'payroll/batches/:batchId',
                element: <BatchDetailPage />,
              },
              {
                path: 'payroll/me/slips/:batchId',
                element: <MySlipDetailPage />,
              },
              {
                path: 'companies/:companyId/payroll/runs',
                element: <RunListPage />,
              },
              { path: 'tax-rules', element: <TaxRuleListPage /> },
              { path: 'tax-rules/new', element: <TaxRuleFormPage /> },
              { path: 'tax-rules/:id', element: <TaxRuleViewPage /> },
              { path: 'tax-rules/:id/edit', element: <TaxRuleFormPage /> },
              { path: 'tax-rules/:id/history', element: <TaxRuleVersionHistoryPage /> },
              {
                path: 'companies/:companyId/payroll/runs/:runId/items/:itemId',
                element: <RunItemDetailPage />,
              },
              {
                path: 'companies/:companyId/payroll/runs/:runId/adjust',
                element: <AdjustmentFormPage />,
              },
              {
                path: 'payroll/batches/:batchId/items/:employeeId',
                element: <RunItemDetailPage />,
              },
              { path: 'payroll/me', element: <MyPayrollHomePage /> },
              { path: 'members', element: <WorkspaceMembersPage /> },
              { path: 'hr', element: <HrWorkspacePage /> },
              { path: 'users', element: <UsersPage /> },
              { path: 'users/new', element: <CreateUserPage /> },
              { path: 'roles', element: <RolesPage /> },
              { path: 'roles/new', element: <CreateRolePage /> },
              { path: 'settings/permissions-matrix', element: <PermissionsMatrixPage /> },
              { path: 'audit-logs', element: <AuditLogsPage /> },
              { path: 'reports', element: <ReportsPage /> },
              { path: 'setup-wizard', element: <Navigate to="/companies" replace /> },
              { path: 'notifications', element: <NotificationsPage /> },
              { path: 'announcements', element: <AnnouncementsPage /> },
              { path: 'organization', element: <OrganizationPage /> },
              { path: 'settings', element: <SettingsPage /> },
              { path: 'billing', element: <BillingPage /> },
              // Where Stripe returns the browser after Checkout. Inside the
              // authenticated layout on purpose: it asks our API what happened
              // rather than believing the URL it was sent back with.
              { path: 'billing/return', element: <BillingReturnPage /> },
              { path: 'settings/organization', element: <Navigate to="/organization" replace /> },
              { path: 'support', element: <SupportPage /> },
              { path: 'support/:id', element: <SupportTicketDetailPage /> },
              { path: 'boards', element: <BoardsPage /> },
              { path: 'boards/archived', element: <BoardsPage /> },
              { path: 'boards/:boardId', element: <BoardViewPage /> },
              {
                path: 'channels',
                element: (
                  <RequireFeature flag="channels.messaging">
                    <ChannelsPage />
                  </RequireFeature>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  { path: '/app', element: <Navigate to="/dashboard" replace /> },
  { path: '*', element: <Navigate to="/" replace /> },
]);

/**
 * App router. Use in App.tsx.
 */
export function AppRouter(): React.ReactElement {
  return <RouterProvider router={router} />;
}
